import React from 'react'
import Head from 'next/head'
import parse from 'html-react-parser'

const MIME_MAP = {
  png: 'image/png',
  svg: 'image/svg+xml',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  ico: 'image/x-icon',
}

const getMimeType = (url) => {
  if (!url) return 'image/png'
  const extension = url.split('.').pop()?.split('?')[0]?.toLowerCase()
  return MIME_MAP[extension] || 'image/png'
}

const faviconSchemaLinks = [
  {
    rel: 'apple-touch-icon-precomposed', sizes: '57x57'
  },
  {
    rel: 'apple-touch-icon-precomposed', sizes: '60x60'
  },
  {
    rel: 'apple-touch-icon-precomposed', sizes: '72x72'
  },
  {
    rel: 'apple-touch-icon-precomposed', sizes: '76x76'
  },
  {
    rel: 'apple-touch-icon-precomposed', sizes: '114x114'
  },
  {
    rel: 'apple-touch-icon-precomposed', sizes: '120x120'
  },
  {
    rel: 'apple-touch-icon-precomposed', sizes: '144x144'
  },
  {
    rel: 'apple-touch-icon-precomposed', sizes: '152x152'
  },
  {
    rel: 'icon', type: 'image/png', sizes: '16x16'
  },
  {
    rel: 'icon', type: 'image/png', sizes: '32x32'
  },
  {
    rel: 'icon', type: 'image/png', sizes: '96x96'
  },
  {
    rel: 'icon', type: 'image/png', sizes: '128x128'
  },
  {
    rel: 'icon', type: 'image/png', sizes: '196x196'
  },
]


const faviconSchemaMeta = [
  {
    name: 'msapplication-TileColor', sizes: '#FFFFFF'
  },
  {
    name: 'msapplication-square70x70logo', sizes: '70x70'
  },
  {
    name: 'msapplication-TileImage', sizes: '144x144'
  },
  {
    name: 'msapplication-square150x150logo', sizes: '150x150'
  },
  {
    name: 'msapplication-wide310x150logo', sizes: '310x150'
  },
  {
    name: 'msapplication-square310x310logo', sizes: '310x310'
  },
]

function getPaginatedCanonical(baseHref, paginationPage) {
  try {
    const canonicalUrl = new URL(baseHref, process.env.NEXT_PUBLIC_SITE_URL)
    canonicalUrl.searchParams.set('page', paginationPage)
    return canonicalUrl.toString()
  } catch {
    const separator = baseHref.includes('?') ? '&' : '?'
    return `${baseHref}${separator}page=${paginationPage}`
  }
}

const Metadata = ({
  yoastMetadata = '',
  favicon = null,
  title = null,
  paginationPage = null,
}) => {
  const parserOptions = {
    replace: (domNode) => {
      if (
        domNode.name === 'link' &&
        domNode.attribs?.rel === 'canonical' &&
        paginationPage
      ) {
        const baseHref = domNode.attribs.href
        return (
          <link
            rel="canonical"
            href={getPaginatedCanonical(baseHref, paginationPage)}
          />
        )
      }
      return undefined
    }
  }

  const fullHead = parse(yoastMetadata, parserOptions)

  return (
    <Head>
      {
        title &&
        <title>{title}</title>
      }

      {fullHead}
      {
        favicon && (() => {
          const firstIconUrl = favicon.sizes['icon-32x32'] || favicon.sizes['icon-16x16']
          const isSvg = firstIconUrl?.toLowerCase().endsWith('.svg')

          if (isSvg) {
            return (
              <link
                rel="icon"
                type="image/svg+xml"
                sizes="any"
                href={firstIconUrl}
              />
            )
          }

          return faviconSchemaLinks.map(props => (
            favicon.sizes[`icon-${props.sizes}`] &&
            <link
              key={props.sizes}
              {...props}
              type={props.type ? getMimeType(favicon.sizes[`icon-${props.sizes}`]) : undefined}
              href={favicon.sizes[`icon-${props.sizes}`]}
            />
          ))
        })()
      }

      {
        favicon &&
        faviconSchemaMeta.map(({
          name, sizes
        }) => (
          favicon.sizes[`icon-${sizes}`] &&
          <meta
            key={sizes}
            name={name}
            content={ favicon.sizes[`icon-${sizes}`]}
          />
        ))
      }

      <meta name="msapplication-TileColor" content="#FFFFFF" />
      <meta id="seodata-metatag" httpEquiv="content-language" content="it" />
      <meta name="theme-color" content="#ffffff" />
    </Head>
  )
}

export default Metadata
